import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Send, Download, RefreshCw, ChevronDown, ChevronUp, FileText,
  Calendar, Euro, Hash, CheckCircle, Clock, ArrowRight, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SepaTransfers = () => {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/sepa-transfers`, { headers });
      setTransfers(response.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des virements SEPA');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  const handleDownloadXml = async (transferId) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/sepa-transfers/${transferId}/xml`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `virement_sepa_${transferId}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Fichier XML telecharge');
    } catch (error) {
      toast.error('Erreur lors du telechargement du fichier XML');
    }
  };

  const handleStatusChange = async (transferId, newStatus) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/sepa-transfers/${transferId}`, { statut: newStatus }, { headers });
      toast.success(`Statut mis a jour: ${getStatusLabel(newStatus)}`);
      fetchTransfers();
    } catch (error) {
      toast.error('Erreur lors de la mise a jour du statut');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const getStatusLabel = (status) => {
    const map = { genere: 'Genere', envoye: 'Envoye', execute: 'Execute', erreur: 'Erreur', annule: 'Annule' };
    return map[status] || status;
  };

  const getStatusBadge = (status) => {
    const config = {
      genere: { cls: 'bg-blue-100 text-blue-800', icon: FileText },
      envoye: { cls: 'bg-yellow-100 text-yellow-800', icon: Send },
      execute: { cls: 'bg-green-100 text-green-800', icon: CheckCircle },
      erreur: { cls: 'bg-red-100 text-red-800', icon: null },
      annule: { cls: 'bg-slate-100 text-slate-800', icon: null }
    };
    const c = config[status] || { cls: 'bg-slate-100 text-slate-800', icon: null };
    const Icon = c.icon;
    return (
      <Badge className={`${c.cls} flex items-center gap-1`}>
        {Icon && <Icon className="w-3 h-3" />}
        {getStatusLabel(status)}
      </Badge>
    );
  };

  const getNextStatuses = (currentStatus) => {
    const flow = {
      genere: ['envoye'],
      envoye: ['execute', 'erreur'],
      execute: [],
      erreur: ['genere'],
      annule: []
    };
    return flow[currentStatus] || [];
  };

  const totalPages = Math.ceil(transfers.length / itemsPerPage);
  const paginatedTransfers = transfers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement des virements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Virements SEPA</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Historique et suivi des virements SEPA generes
          </p>
        </div>
        <Button variant="outline" onClick={fetchTransfers}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Send className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total virements</p>
                <p className="text-2xl font-bold">{transfers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">En attente</p>
                <p className="text-2xl font-bold">
                  {transfers.filter(t => t.statut === 'genere' || t.statut === 'envoye').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Executes</p>
                <p className="text-2xl font-bold">
                  {transfers.filter(t => t.statut === 'execute').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Euro className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Montant total</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(transfers.reduce((sum, t) => sum + (t.montant_total || 0), 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {transfers.length === 0 ? (
            <div className="text-center py-12">
              <Send className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg font-medium">Aucun virement SEPA</p>
              <p className="text-slate-400 text-sm mt-1">Les virements generes apparaitront ici</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Date execution</TableHead>
                    <TableHead className="text-center">Nb virements</TableHead>
                    <TableHead className="text-right">Montant total</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTransfers.map((transfer) => {
                    const isExpanded = expandedRow === transfer.id;
                    const nextStatuses = getNextStatuses(transfer.statut);
                    return (
                      <React.Fragment key={transfer.id}>
                        <TableRow
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => setExpandedRow(isExpanded ? null : transfer.id)}
                        >
                          <TableCell>
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            )}
                          </TableCell>
                          <TableCell className="font-medium font-mono">
                            {transfer.reference || transfer.id?.slice(0, 12)}
                          </TableCell>
                          <TableCell>{formatDate(transfer.date_execution)}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">
                              <Hash className="w-3 h-3 mr-1" />
                              {transfer.nb_virements || (transfer.invoices || []).length || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(transfer.montant_total)}
                          </TableCell>
                          <TableCell>{getStatusBadge(transfer.statut)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadXml(transfer.id)}
                                title="Telecharger XML"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              {nextStatuses.length > 0 && (
                                <Select onValueChange={(v) => handleStatusChange(transfer.id, v)}>
                                  <SelectTrigger className="w-32 h-8 text-xs">
                                    <SelectValue placeholder="Changer statut" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {nextStatuses.map(s => (
                                      <SelectItem key={s} value={s}>
                                        <div className="flex items-center gap-1">
                                          <ArrowRight className="w-3 h-3" />
                                          {getStatusLabel(s)}
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>

                        {/* Expanded row - included invoices */}
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-slate-50 p-0">
                              <div className="p-4">
                                <h4 className="text-sm font-semibold text-slate-700 mb-3">
                                  Factures incluses dans ce virement
                                </h4>
                                {(transfer.invoices || transfer.factures || []).length === 0 ? (
                                  <p className="text-sm text-slate-400">Aucune facture associee</p>
                                ) : (
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="text-xs">Reference facture</TableHead>
                                        <TableHead className="text-xs">Fournisseur</TableHead>
                                        <TableHead className="text-xs text-right">Montant</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {(transfer.invoices || transfer.factures || []).map((inv, idx) => (
                                        <TableRow key={idx}>
                                          <TableCell className="text-sm font-mono">
                                            {inv.reference || inv.invoice_reference || '-'}
                                          </TableCell>
                                          <TableCell className="text-sm">
                                            {inv.supplier_name || inv.fournisseur || '-'}
                                          </TableCell>
                                          <TableCell className="text-sm text-right font-medium">
                                            {formatCurrency(inv.montant || inv.montant_ttc)}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t">
                  <p className="text-sm text-slate-500">
                    {transfers.length} virement(s) - Page {currentPage} / {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SepaTransfers;
